import { useState, useEffect, useRef } from "react";
import useConditionalEffect from "../../../hooks/useConditionalEffect";
import { registerDrawHandler, clearAllHandlers, updateLineFromExistingPolygon, COUNTRY_PRESETS, detectCountryForFeature } from "../../../utils/drawUtils";
import { clearLayers, add3DBuildingsLayer } from "../../../utils/layerUtils";
import { clearBufferLayers, removeBuffersForPolyId, syncBuffersFor } from "../../../utils/bufferUtils";
import { clearAssetPaintOverlay, ensureAssetPaintOverlay, tagAndPaintAssetRisk, refreshAssetPaintOverlay } from "../../../utils/assetRiskUtils";
import SatelliteDrawControls from "./SatelliteDrawControls";
import SatelliteInfo from "./SatelliteInfo";
import { useMap } from "../../../hooks/useMap";
import { useDraw } from "../../../hooks/useDraw";
import { computeMaxAssetRiskFrom } from "../../../utils/calculateResult";

const Satellite = ({
  drawModeRef,
  isSatellite,
  drawMode,
  setDrawMode,
  result,
  setResult,
  whichMode,
  setWichMode,
  setCalculatedResult,
  calculatedHandler,
  isOpenMenu,
  handleOpenMenu,
  setRiskInfo
}) => {
  const map = useMap();
  const draw = useDraw();
  const [activePolyId, setActivePolyId] = useState(null);
  const [polygon, setPolygon] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [bufferDistances, setBufferDistances] = useState([1.5, 9, 30]);
  const buildingKeyToDrawIdRef = useRef(new Map());
  const drawIdToBuildingKeyRef = useRef(new Map());

  const drawPolyLayerIdsRef = useRef([]);

  useConditionalEffect(
    () => registerDrawHandler(map, draw, setResult, setDrawMode, whichMode, setRiskInfo, bufferDistances, setPolygon, setExpanded),
    [drawMode[whichMode], whichMode, bufferDistances]
  );

  const getBuildingKey = (feature) => {
    const pid = feature.id ?? feature.properties?.id ?? feature.properties?.osm_id ?? feature.properties?.["@id"];
    if (pid != null) return `id:${pid}`;

    const geom = feature.geometry;
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    const scan = (coords) => {
      for (const c of coords) {
        if (Array.isArray(c[0])) scan(c);
        else {
          const [x, y] = c;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    };
    scan(geom.coordinates);
    const r = (n) => n.toFixed(5);
    return `bbox:${r(minX)},${r(minY)},${r(maxX)},${r(maxY)}`;
  };

  const clickHitsDrawPolygon = (e) => {
    const ids = drawPolyLayerIdsRef.current;
    if (!map || !ids.length) return false;
    let point = e?.point;
    if (!point && e?.lngLat && typeof map.project === 'function') {
      const p = map.project(e.lngLat);
      point = { x: p.x, y: p.y };
    }
    if (!point) return false;
    const bbox = [[point.x - 4, point.y - 4], [point.x + 4, point.y + 4]];
    const hits = map.queryRenderedFeatures(bbox, { layers: ids });
    return !!(hits && hits.length);
  };

  useEffect(() => {
    if (!map) return;
    ensureAssetPaintOverlay(map);
  }, [map]);

  useEffect(() => {
    if (!map || !draw) return;

    add3DBuildingsLayer(map);
    const assetContourActive = drawMode.home === "draw_polygon";

    let buildingsClickEnabled = false;
    let buildingsMapClickBound = false;
    const enableBuildingsClick = () => {
      if (!buildingsClickEnabled && map.getLayer("3d-buildings")) {
        map.on("click", "3d-buildings", handleBuildingClick);
        map.on("mouseenter", "3d-buildings", pointerEnter);
        map.on("mouseleave", "3d-buildings", pointerLeave);
        buildingsClickEnabled = true;
      }
      if (!buildingsMapClickBound && map.getLayer('3d-buildings')) {
        map.on('click', handleBuildingClick);
        map.on('touchend', handleBuildingClick);
        map.on('pointerup', handleBuildingClick);
        buildingsMapClickBound = true;
      }
    };
    const disableBuildingsClick = () => {
      if (buildingsClickEnabled) {
        if (map.getLayer("3d-buildings")) {
          map.off("click", "3d-buildings", handleBuildingClick);
          map.off("mouseenter", "3d-buildings", pointerEnter);
          map.off("mouseleave", "3d-buildings", pointerLeave);
        }
        buildingsClickEnabled = false;
        pointerLeave();
      }
      if (buildingsMapClickBound) {
        map.off('click', handleBuildingClick);
        map.off('touchend', handleBuildingClick);
        map.off('pointerup', handleBuildingClick);
        buildingsMapClickBound = false;
      }
    };

    const pointerEnter = () => {
      if (assetContourActive) map.getCanvas().style.cursor = "pointer";
    };
    const pointerLeave = () => {
      map.getCanvas().style.cursor = "";
    };

    const onModeChange = (ev) => {
      const m = ev?.mode || "";
      if (m === "direct_select" || m.startsWith?.("draw_")) disableBuildingsClick();
      else if (assetContourActive) enableBuildingsClick();
    };
    map.on("draw.modechange", onModeChange);

    async function handleBuildingClick(e) {
      if (clickHitsDrawPolygon(e) || !assetContourActive) return;

      let building = e.features?.[0];

      if (!building) {
        let point = e?.point;
        if (!point && e?.lngLat && typeof map.project === 'function') {
          const p = map.project(e.lngLat);
          point = { x: p.x, y: p.y };
        }

        if (point) {
          const bbox = [[point.x - 8, point.y - 8], [point.x + 8, point.y + 8]];
          let hits = [];
          try { hits = map.queryRenderedFeatures(bbox, { layers: ['3d-buildings'] }) || []; } catch {}
          building = hits && hits.length ? hits[0] : null;
        }
      }

      if (!building?.geometry) return;

      const key = getBuildingKey(building);
      const existingId = buildingKeyToDrawIdRef.current.get(key);
      const existing = existingId ? draw.get(existingId) : null;

      if (existing) {
        draw.changeMode("simple_select");
        map.once("draw.render", () => {
          draw.changeMode("direct_select", { featureId: existingId });
        });
        setActivePolyId(existingId);
        setPolygon(existing);
        const b = existing.properties?.buffers || [1.5, 9, 30];
        setBufferDistances([...b]);
        setExpanded(true);
        setWichMode("home");
        setDrawMode((prev) => ({ ...prev, home: "simple_select", area: "simple_select", point: "simple_select" }));
        return;
      } else if (buildingKeyToDrawIdRef.current.has(key)) {
        buildingKeyToDrawIdRef.current.delete(key);
      }
      const detectedCountry = detectCountryForFeature(building);
      const preset = (COUNTRY_PRESETS?.[detectedCountry] || [1.5, 9, 30]).slice();

      const poly = {
        type: "Feature",
        geometry: building.geometry,
        properties: {
          isBuilding: true,
          buffers: preset,
          country: detectedCountry,
        },
      };

      const newId = draw.add(poly);
      buildingKeyToDrawIdRef.current.set(key, newId);
      drawIdToBuildingKeyRef.current.set(newId, key);

      try {
        await tagAndPaintAssetRisk(map, draw, draw.get(newId), setRiskInfo);
      } catch { }
      const created = draw.get(newId);
      setActivePolyId(newId);
      setPolygon(created);
      setBufferDistances(preset);
      setExpanded(true);

      syncBuffersFor(created, map, preset);

      setWichMode("home");
      setDrawMode((prev) => ({ ...prev, home: "simple_select", area: "simple_select", point: "simple_select" }));
      map.once("draw.render", () => {
        draw.changeMode("direct_select", { featureId: newId });
      });

      ensureAssetPaintOverlay(map);
      refreshAssetPaintOverlay(map, draw);
    }

    const onDrawDeleteCleanup = (e) => {
      e.features?.forEach((f) => {
        if (!f?.id) return;
        const id = f.id;
        const key = drawIdToBuildingKeyRef.current.get(id);
        if (key) {
          buildingKeyToDrawIdRef.current.delete(key);
          drawIdToBuildingKeyRef.current.delete(id);
        }
      });
    };
    map.on("draw.delete", onDrawDeleteCleanup);

    if (assetContourActive) enableBuildingsClick();
    else disableBuildingsClick();

    return () => {
      disableBuildingsClick();
      map.off("draw.modechange", onModeChange);
      map.off("draw.delete", onDrawDeleteCleanup);
    };
  }, [map, draw, drawMode.home]);

  useEffect(() => {
    if (!map || !draw) return;

    const onKeyDown = (e) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;

      const tag = (document.activeElement && document.activeElement.tagName) || "";
      const typing = /INPUT|TEXTAREA|SELECT/.test(tag) || document.activeElement?.isContentEditable;
      if (typing) return;

      e.preventDefault();
      handleContextualDelete();
      const info = computeMaxAssetRiskFrom(draw);
      setRiskInfo(info);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [map, draw, activePolyId]);

  const handleDeleteAssetContour = (polygon) => {
    if (!map || !draw) return;
    const id = polygon.id;

    if (polygon?.properties?.isBuilding || polygon?.properties?.assetContour) {
      removeBuffersForPolyId(map, id);
    }

    draw.delete(id);
    setActivePolyId(null);
    setPolygon(null);
    setExpanded(false);
    refreshAssetPaintOverlay(map, draw);
    return;
  }

  const handleContextualDelete = () => {
    if (!map || !draw) return;
    
    const mode = draw.getMode();
    
    if (mode === "direct_select") {
      const selectedFeatures = draw.getSelected?.();
      const pts = draw.getSelectedPoints?.();
      const hasVertex = !!(pts && pts.features && pts.features.length);

      if (hasVertex) {
        const selectedPoly = selectedFeatures?.features?.[0];

        if (selectedPoly?.properties?.lotPerimeter) {
          const vertexCount = getLotPerimeterVertexCount(selectedPoly);
          if (vertexCount > 3) {
            draw.trash();
            setTimeout(() => {
              const updatedPoly = draw.get(selectedPoly.id);
              if (updatedPoly) {
                updateLineFromExistingPolygon(map, updatedPoly, setResult);
              }
            }, 100);
          }
          return;
        }

        if (getActivePolyVertexCount() > 3) {
          draw.trash();
        }
        return;
      } else {
        const selectedFeatures = draw.getSelected?.();
        const selectedPoly = selectedFeatures?.features?.[0];

        if (selectedPoly?.properties?.lotPerimeter) {
          if (map.getLayer("line")) map.removeLayer("line");
          if (map.getLayer("line-outline")) map.removeLayer("line-outline");
          if (map.getSource("line")) map.removeSource("line");

          try {
            const mode = draw.getMode?.();
            if (mode?.name === "direct_select" || mode?.mode === "direct_select") {
              draw.changeMode("simple_select");
            }
          } catch { }

          draw.delete(selectedPoly.id);
          setActivePolyId(null);
          setPolygon(null);
          return;
        }
      }

      if (selectedFeatures?.features?.[0]?.properties?.isBuilding || selectedFeatures?.features?.[0]?.properties?.assetContour) {
        handleDeleteAssetContour(selectedFeatures.features[0]);
        return;
      }
    }

    if (mode === "simple_select" && activePolyId) {
      const poly = draw.get(activePolyId);

      handleDeleteAssetContour(poly);
      return;
    }

    draw.trash();
  };

  const getLotPerimeterVertexCount = (polygon) => {
    if (!polygon || polygon.geometry?.type !== "Polygon") return 0;
    const ring = polygon.geometry.coordinates?.[0] || [];
    return Math.max(0, ring.length - 1);
  };

  const getActivePolyVertexCount = () => {
    const p = draw?.get(activePolyId);
    const ring = p?.geometry?.type === "Polygon" ? p.geometry.coordinates?.[0] || [] : [];
    return Math.max(0, ring.length - 1);
  };

  useEffect(() => {
    if (!map || !draw) return;

    const onSelect = (e) => {
      const poly = e.features?.[0];

      if (!poly) {
        setActivePolyId(null);
        setExpanded(false);
        return;
      }

      setActivePolyId(poly.id);
      setPolygon(poly);

      if (poly.properties?.isBuilding || poly.properties?.assetContour) {
        const b = poly.properties?.buffers || [1.5, 9, 30];
        setBufferDistances([...b]);
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    };

    map.on("draw.selectionchange", onSelect);
    return () => map.off("draw.selectionchange", onSelect);
  }, [map, draw]);

  const toggleDrawModeArea = () => {
    setWichMode("area");
    setDrawMode((prev) => ({
      ...prev,
      area: prev.area === "simple_select" ? "draw_polygon" : "simple_select",
      home: "simple_select",
      point: "simple_select",
    }));

    if (drawModeRef.current && drawMode.area === "simple_select") {
      drawModeRef.current("simple_select");
      drawModeRef.current("draw_polygon");
    } else {
      drawModeRef.current("simple_select");
    }
  };

  const toggleDrawModeHome = () => {
    calculatedHandler(false);
    setWichMode("home");
    setDrawMode((prev) => ({
      ...prev,
      home: prev.home === "simple_select" ? "draw_polygon" : "simple_select",
      area: "simple_select",
      point: "simple_select",
    }));

    if (drawModeRef.current && drawMode.home === "simple_select") {
      drawModeRef.current("simple_select");
      drawModeRef.current("draw_polygon");
    } else {
      drawModeRef.current("simple_select");
    }
  };

  const clearHandler = () => {
    clearLayers(map);
    clearBufferLayers(map);
    clearAllHandlers(map);
    clearAssetPaintOverlay(map);
    ensureAssetPaintOverlay(map);

    setDrawMode((prev) => ({
      ...prev,
      area: "simple_select",
      home: "simple_select",
      point: "simple_select",
    }));

    setResult(null);
    setCalculatedResult(0);
    setPolygon(null);
    setActivePolyId(null);
    setExpanded(false);
    setWichMode("area");
    setRiskInfo(null);

    draw.changeMode("simple_select");
    draw.deleteAll();

    if (map.__lotRing) {
      map.__lotRing = null;
    }
  };

  return (
    <>
      <SatelliteDrawControls
        toggleDrawModeArea={toggleDrawModeArea}
        toggleDrawModeHome={toggleDrawModeHome}
        drawMode={drawMode}
        clearHandler={clearHandler}
        expanded={expanded}
        bufferDistances={bufferDistances}
        setBufferDistances={setBufferDistances}
        activePolyId={activePolyId}
        isOpenMenu={isOpenMenu}
        handleOpenMenu={handleOpenMenu}
      />
      {isSatellite && result && <SatelliteInfo result={result} />}
    </>
  );
};

export default Satellite;
